import { useState, useEffect } from 'react';
import logger from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Package2, FolderKanban, Package, Share2, BarChart3, Heart, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import ShareStashModal from '@/components/ShareStashModal';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const [stats, setStats] = useState(null);
  const [communityStats, setCommunityStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
    loadCommunityStats();
  }, []);

  const loadCommunityStats = async () => {
    try {
      const response = await axios.get(`${API}/community/stats`);
      setCommunityStats(response.data);
    } catch (error) {
      logger.error('Failed to load community stats:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      logger.error('Failed to load stats:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-2">
                {t('dashboard.welcome')}
              </h1>
              <p className="text-lg text-muted-foreground font-body">
                {t('dashboard.subtitle')}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/statistics')}
                className="rounded-full"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                {isEnglish ? 'Statistics' : 'Tilastot'}
              </Button>
              <Button
                onClick={() => setShareModalOpen(true)}
                className="rounded-full bg-primary text-primary-foreground"
              >
                <Share2 className="h-4 w-4 mr-2" />
                {isEnglish ? 'Share Stash' : 'Jaa Varasto'}
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div 
            data-testid="stat-total-yarns"
            className="rounded-3xl bg-white border border-stone-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Package2 className="h-6 w-6 text-primary" />
              </div>
              <span className="text-4xl font-heading font-bold text-primary">{stats?.total_yarns || 0}</span>
            </div>
            <h3 className="text-lg font-heading font-semibold">{t('dashboard.stats.totalYarns')}</h3>
            <p className="text-sm text-muted-foreground font-body">{t('dashboard.stats.skeinsInStash')}</p>
          </div>

          <div 
            data-testid="stat-total-projects"
            className="rounded-3xl bg-white border border-stone-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="h-12 w-12 rounded-full bg-secondary/10 flex items-center justify-center">
                <Package className="h-6 w-6 text-secondary" />
              </div>
              <span className="text-4xl font-heading font-bold text-secondary">{stats?.total_projects || 0}</span>
            </div>
            <h3 className="text-lg font-heading font-semibold">{t('dashboard.stats.allProjects')}</h3>
            <p className="text-sm text-muted-foreground font-body">{t('dashboard.stats.totalCreated')}</p>
          </div>

          <div 
            data-testid="stat-active-projects"
            className="rounded-3xl bg-white border border-stone-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center">
                <FolderKanban className="h-6 w-6 text-accent" />
              </div>
              <span className="text-4xl font-heading font-bold text-accent">{stats?.active_projects || 0}</span>
            </div>
            <h3 className="text-lg font-heading font-semibold">{t('dashboard.stats.activeProjects')}</h3>
            <p className="text-sm text-muted-foreground font-body">{t('dashboard.stats.inProgress')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="rounded-3xl bg-white border border-stone-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-bold">{t('dashboard.recentYarns')}</h2>
              <Button
                data-testid="view-all-yarns-button"
                variant="ghost"
                onClick={() => navigate('/inventory')}
                className="rounded-lg text-primary hover:text-primary hover:bg-rose-50 font-medium"
              >
                {t('dashboard.viewAll')}
              </Button>
            </div>
            {stats?.recent_yarns && stats.recent_yarns.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_yarns.map((yarn) => (
                  <div
                    key={yarn.yarn_id}
                    data-testid={`recent-yarn-${yarn.yarn_id}`}
                    onClick={() => navigate(`/inventory/${yarn.yarn_id}`)}
                    className="flex items-center space-x-4 p-4 rounded-2xl bg-stone-50 hover:bg-stone-100 cursor-pointer transition-colors"
                  >
                    <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-rose-100 to-orange-100 flex-shrink-0 flex items-center justify-center">
                      <Package2 className="h-8 w-8 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-semibold truncate">
                        {yarn.brand || 'Unknown Brand'} - {yarn.color || 'Unknown Color'}
                      </p>
                      <p className="text-sm text-muted-foreground font-body">
                        {yarn.weight || 'Unknown Weight'} {yarn.fiber_content && `• ${yarn.fiber_content}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package2 className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground font-body mb-4">{t('dashboard.noYarns')}</p>
                <Button
                  data-testid="add-first-yarn-button"
                  onClick={() => navigate('/inventory/add')}
                  className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('dashboard.addFirstYarn')}
                </Button>
              </div>
            )}
          </div>

          <div className="rounded-3xl bg-white border border-stone-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-bold">{t('dashboard.quickActions')}</h2>
            </div>
            <div className="space-y-4">
              <Button
                data-testid="quick-add-yarn-button"
                onClick={() => navigate('/inventory/add')}
                className="w-full rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold py-6 text-lg justify-start"
              >
                <Plus className="h-5 w-5 mr-3" />
                {t('dashboard.addNewYarn')}
              </Button>
              <Button
                data-testid="quick-start-project-button"
                onClick={() => navigate('/projects/add')}
                className="w-full rounded-full bg-white text-primary border-2 border-primary/20 hover:border-primary/50 hover:bg-rose-50 transition-all duration-300 font-bold py-6 text-lg justify-start"
              >
                <FolderKanban className="h-5 w-5 mr-3" />
                {t('dashboard.startNewProject')}
              </Button>
              <Button
                data-testid="quick-view-projects-button"
                onClick={() => navigate('/projects')}
                variant="ghost"
                className="w-full rounded-full text-stone-600 hover:text-primary hover:bg-rose-50 font-medium py-6 text-lg justify-start"
              >
                {t('dashboard.viewAllProjects')}
              </Button>
            </div>
          </div>
        </div>

        {/* Community Stats - Social Proof */}
        {communityStats && (communityStats.yarns > 0 || communityStats.projects > 0) && (
          <div className="mt-8 rounded-3xl bg-gradient-to-r from-stone-50 to-stone-100 border border-stone-200 p-6 shadow-sm">
            <h3 className="text-lg font-heading font-bold mb-4 text-center">
              {isEnglish ? 'Even Rounds Community' : 'Even Rounds -yhteisö'}
            </h3>
            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Package2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xl font-heading font-bold text-primary">
                    {communityStats.yarns.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isEnglish ? 'yarns' : 'lankaa'}
                  </p>
                </div>
              </div>
              
              <div className="hidden sm:block w-px h-10 bg-stone-200" />
              
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-secondary/10 flex items-center justify-center">
                  <FolderKanban className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <p className="text-xl font-heading font-bold text-secondary">
                    {communityStats.projects.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isEnglish ? 'projects' : 'projektia'}
                  </p>
                </div>
              </div>
              
              <div className="hidden sm:block w-px h-10 bg-stone-200" />
              
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="text-xl font-heading font-bold text-accent">
                    {communityStats.users.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isEnglish ? 'crafters' : 'käsityöläistä'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Support Even Rounds Section */}
        <div className="mt-8 rounded-3xl bg-gradient-to-br from-rose-50 to-amber-50 border border-rose-100 p-8 shadow-sm">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-rose-100">
                <Heart className="h-7 w-7 text-rose-500" />
              </div>
            </div>
            <div className="flex-grow text-center md:text-left">
              <h3 className="text-xl font-heading font-bold mb-2">
                {t('landing.support.title', 'Support Even Rounds')}
              </h3>
              <p className="text-stone-600 text-sm">
                {t('landing.support.subtitle', 'Your support helps us continue development and keep the service running.')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Button
                onClick={() => window.open('https://buy.stripe.com/test_00g5kU8Ss8I6eWc000', '_blank')}
                className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-medium"
              >
                <Heart className="h-4 w-4 mr-2" />
                {t('landing.support.monthly', 'Monthly Support')}
              </Button>
              <Button
                onClick={() => window.open('https://buy.stripe.com/test_14k5kUfgQ8I66pGcMN', '_blank')}
                variant="outline"
                className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50 font-medium"
              >
                {t('landing.support.oneTime', 'One-time Donation')}
              </Button>
            </div>
          </div>
        </div>
      </main>
      
      {/* Share Stash Modal */}
      <ShareStashModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
      />
    </div>
  );
}
